import React from 'react';
import { useTranslation } from 'react-i18next';

import type { OpeningHoursDay, Retailer } from '../../@types/Retailer';
import { getMarket } from '../../i18n';
import { parse } from '../../utils/phonenumbers';

interface OpeningHoursProps {
  retailer: Retailer;
}

const OpeningHours = ({ retailer }: OpeningHoursProps) => {
  const { t } = useTranslation(['glossary', 'retailer']);
  if (!retailer?.openingHours) {
    return null;
  }

  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayNameLabels = [
    t('sunday', { ns: 'glossary' }),
    t('monday', { ns: 'glossary' }),
    t('tuesday', { ns: 'glossary' }),
    t('wednesday', { ns: 'glossary' }),
    t('thursday', { ns: 'glossary' }),
    t('friday', { ns: 'glossary' }),
    t('saturday', { ns: 'glossary' }),
  ];

  const diff = new Date().getDay();
  const todayNameKey = dayNames[new Date().getDay()];
  const tomorrowNameKey = dayNames[(new Date().getDay() + 1) % 7];
  let phoneNumberView = null;

  if (retailer.telephoneNumber && retailer.telephoneNumber.length > 3) {
    const { marketCode } = getMarket();
    const phoneNumber = parse(retailer.telephoneNumber, marketCode);

    if (phoneNumber?.isValid()) {
      phoneNumberView = (
        <div className="fluid-row">
          <div className="col-6 m-b-mini">
            <div className="text-dark-lighten font-size-small">
              {t('phone', { ns: 'retailer' })}
            </div>
          </div>
          <div className="col-6 m-b-mini">{phoneNumber.formatNational()}</div>
        </div>
      );
    }
  }

  return (
    <div className="map-nav">
      <div className="map-nav-content">
        <div className="map-nav-body">
          <div className="body-section">
            <h4 className="m-b">{t('openingHours', { ns: 'retailer' })}</h4>
            <div className="m-b">
              {[0, 1, 2, 3, 4, 5, 6].map((index) => {
                const dayName = dayNames[(index + diff) % 7];
                let dayNameLabel = dayNameLabels[(index + diff) % 7];
                const openingHours = retailer.openingHours?.[dayName as OpeningHoursDay];

                if (dayName === todayNameKey) {
                  dayNameLabel = t('today', { ns: 'retailer' });
                }
                if (dayName === tomorrowNameKey) {
                  dayNameLabel = t('tomorrow', { ns: 'retailer' });
                }

                return (
                  <div key={dayNameLabel} className="fluid-row">
                    <div className="col-6 m-b-mini">
                      <div className="text-dark-lighten font-size-small">{dayNameLabel}</div>
                    </div>
                    <div className="col-6 m-b-mini">
                      {openingHours?.openFrom}-{openingHours?.openTo}
                    </div>
                  </div>
                );
              })}
            </div>
            <h4 className="m-b">{t('contact', { ns: 'retailer' })}</h4>
            {phoneNumberView}
            <div className="fluid-row">
              <div className="col-6 m-b-mini">
                <div className="text-dark-lighten font-size-small">
                  {t('visitingAddress', { ns: 'retailer' })}
                </div>
              </div>
              <div className="col-6 m-b-mini">
                {retailer.position.street}, {retailer.position.city}
              </div>
            </div>
          </div>
          <a
            href={`https://www.google.com/maps/dir//${retailer.position.street},${retailer.position.city}`}
            data-am-button="action full-width square"
            target="_blank"
            rel="nofollow noopener noreferrer"
          >
            <i className="icon-map-pin m-r-half" />
            {t('visitUs', { ns: 'retailer' })}
          </a>
        </div>
      </div>
    </div>
  );
};

export default OpeningHours;
